export type Nominee = {
  givenName: string
  familyName: string
  email: string
}

export type UserOrNominee = {
  userId?: number
  nominee?: Nominee
}

export type ApplicationAssignment = {
  userId?: number
  nominee?: Nominee
  roleIds: number[]
}

export type ApplicationAccessBlock = {
  applicationId: number
  assignments: ApplicationAssignment[]
}

export function personKey(entry: UserOrNominee | ApplicationAssignment): string | undefined {
  if (entry.userId != null) return `user:${entry.userId}`
  const email = entry.nominee?.email?.trim().toLowerCase()
  if (email) return `email:${email}`
  return undefined
}

export function isCompletePerson(entry: UserOrNominee): boolean {
  if (entry.userId != null && entry.userId > 0) return true
  const nominee = entry.nominee
  return Boolean(
    nominee?.givenName?.trim() && nominee.familyName?.trim() && nominee.email?.trim() && nominee.email.includes('@')
  )
}

export function isBlankPerson(entry: UserOrNominee): boolean {
  if (entry.userId != null) return false
  const nominee = entry.nominee
  if (!nominee) return true
  return !nominee.givenName?.trim() && !nominee.familyName?.trim() && !nominee.email?.trim()
}

export function uniquePeople(people: UserOrNominee[]): UserOrNominee[] {
  const seen = new Set<string>()
  const unique: UserOrNominee[] = []
  for (const person of people) {
    if (!isCompletePerson(person)) continue
    const key = personKey(person)
    if (!key || seen.has(key)) continue
    seen.add(key)
    unique.push(person)
  }
  return unique
}

/** One assignment per nominated person, keeping roles already picked for that person. */
export function assignmentsFromPeople(
  people: UserOrNominee[],
  existing: ApplicationAssignment[]
): ApplicationAssignment[] {
  return uniquePeople(people).map((person) => {
    const key = personKey(person)
    const previous = existing.find((assignment) => personKey(assignment) === key)
    return {
      userId: person.userId,
      nominee: person.nominee,
      roleIds: previous?.roleIds ?? [],
    }
  })
}

export type ProjectAccessTicket = {
  projectId: number
  applications: ApplicationAccessBlock[]
}

export type ProjectCreateTicket = {
  projectName: string
  frequency: string
  projectLeaders: UserOrNominee[]
  projectAdministrators?: UserOrNominee[]
  projectMembers?: UserOrNominee[]
  applications: ApplicationAccessBlock[]
}

export type AddOrganisationTicket = ProjectCreateTicket & {
  organisation: {
    name: string
    longName: string
    description: string
    domain: string
  }
}

export type WorkspaceCreateTicket = {
  projectId: number
  applicationId: number
  people: ApplicationAssignment[]
}

export const PROJECT_FREQUENCY_OPTIONS = ['Daily', 'Weekly', 'Monthly', 'Annually', 'Ad hoc'] as const

export const REQUEST_STATUS_OPEN = 1
export const REQUEST_STATUS_CLOSED = 2
export const REQUEST_STATUS_IN_PROGRESS = 3

export const REQUEST_TICKET_TYPE_PROJECT_ACCESS = 1
export const REQUEST_TICKET_TYPE_PROJECT_CREATE = 2
export const REQUEST_TICKET_TYPE_ADD_ORGANISATION = 3
export const REQUEST_TICKET_TYPE_WORKSPACE_CREATE = 4

export const REQUEST_STATUS_LABELS: Record<number, string> = {
  [REQUEST_STATUS_OPEN]: 'Open',
  [REQUEST_STATUS_CLOSED]: 'Closed',
  [REQUEST_STATUS_IN_PROGRESS]: 'In Progress',
}

export const REQUEST_TICKET_TYPE_LABELS: Record<number, string> = {
  [REQUEST_TICKET_TYPE_PROJECT_ACCESS]: 'Project Access',
  [REQUEST_TICKET_TYPE_PROJECT_CREATE]: 'Project Create',
  [REQUEST_TICKET_TYPE_ADD_ORGANISATION]: 'Add Organisation',
  [REQUEST_TICKET_TYPE_WORKSPACE_CREATE]: 'Workspace Create',
}

const REQUEST_STATUS_ALIASES: Record<string, number> = {
  '1': REQUEST_STATUS_OPEN,
  open: REQUEST_STATUS_OPEN,
  '2': REQUEST_STATUS_CLOSED,
  closed: REQUEST_STATUS_CLOSED,
  '3': REQUEST_STATUS_IN_PROGRESS,
  inprogress: REQUEST_STATUS_IN_PROGRESS,
  'in progress': REQUEST_STATUS_IN_PROGRESS,
}

const REQUEST_TICKET_TYPE_ALIASES: Record<string, number> = {
  '1': REQUEST_TICKET_TYPE_PROJECT_ACCESS,
  projectaccess: REQUEST_TICKET_TYPE_PROJECT_ACCESS,
  'project access': REQUEST_TICKET_TYPE_PROJECT_ACCESS,
  '2': REQUEST_TICKET_TYPE_PROJECT_CREATE,
  projectcreate: REQUEST_TICKET_TYPE_PROJECT_CREATE,
  'project create': REQUEST_TICKET_TYPE_PROJECT_CREATE,
  '3': REQUEST_TICKET_TYPE_ADD_ORGANISATION,
  addorganisation: REQUEST_TICKET_TYPE_ADD_ORGANISATION,
  addorganization: REQUEST_TICKET_TYPE_ADD_ORGANISATION,
  'add organisation': REQUEST_TICKET_TYPE_ADD_ORGANISATION,
  '4': REQUEST_TICKET_TYPE_WORKSPACE_CREATE,
  workspacecreate: REQUEST_TICKET_TYPE_WORKSPACE_CREATE,
  'workspace create': REQUEST_TICKET_TYPE_WORKSPACE_CREATE,
}

function coerceEnumValue(value: unknown, aliases: Record<string, number>): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const alias = aliases[value.trim().toLowerCase()]
    if (alias != null) return alias
    const asNumber = Number(value)
    if (Number.isFinite(asNumber)) return asNumber
  }
  return undefined
}

/** API may send numeric enums or Newtonsoft string names (`open`, `addOrganisation`). */
export function coerceRequestStatus(value: unknown): number | undefined {
  return coerceEnumValue(value, REQUEST_STATUS_ALIASES)
}

export function coerceTicketType(value: unknown): number | undefined {
  return coerceEnumValue(value, REQUEST_TICKET_TYPE_ALIASES)
}

export function getRequestStatusLabel(value: unknown): string {
  return REQUEST_STATUS_LABELS[coerceRequestStatus(value) ?? 0] ?? 'Unknown'
}

export function getTicketTypeLabel(value: unknown): string {
  return REQUEST_TICKET_TYPE_LABELS[coerceTicketType(value) ?? 0] ?? 'Unknown'
}

export function coerceNumericId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

export function formatPersonName(
  givenName?: string | null,
  familyName?: string | null,
  email?: string | null
): string {
  const name = `${givenName ?? ''} ${familyName ?? ''}`.trim()
  if (name && email?.trim()) return `${name} (${email.trim()})`
  if (name) return name
  return email?.trim() || ''
}

export type UserOptionLike = { value: number; label?: string; description?: string }

export function userLabelFromOptions(userId: number | undefined, options: UserOptionLike[]): string | undefined {
  if (userId == null) return undefined
  const option = options.find((entry) => entry.value === userId)
  if (!option) return undefined
  return formatPersonName(option.label, '', option.description) || option.label?.trim() || option.description?.trim()
}

export function formatUserOrNominee(entry: UserOrNominee, userLabel?: string): string {
  const userId = coerceNumericId(entry.userId)
  if (userId != null && userLabel) return userLabel
  if (entry.nominee) {
    const nomineeLabel = formatPersonName(entry.nominee.givenName, entry.nominee.familyName, entry.nominee.email)
    if (nomineeLabel) return nomineeLabel
  }
  if (userId != null) return `User #${userId}`
  return 'Unknown'
}
